import { reflect } from '@effector/reflect';
import { Checkbox, Select, Stack, Text, TextInput } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { Plus } from 'lucide-react';
import { not } from 'patronum';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ACCOUNT_NAME_MIN_LENGTH, ACCOUNT_NOTE_MAX_LENGTH } from 'entities/account';
import { viewerModel } from 'entities/viewer';
import { translated, $t } from 'shared/lib';
import { createModalForm, ModalFormProps, MultiSelect } from 'shared/ui';
import { AddAccountModel } from '../../model';
import cls from './add-account-modal.module.scss';

export const createAddAccountModal = (model: AddAccountModel) => {
	const ModalForm = createModalForm(model);

	const NameField = reflect({
		view: TextInput,
		bind: {
			'data-test-id': 'add-account-name-input',
			label: translated('add-account.name'),
			placeholder: translated('add-account.name'),
			errorProps: { 'data-test-id': 'add-account-name-input-error' },
			error: combine(
				[model.form.fields.name.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-account.name'),
						minLength: ACCOUNT_NAME_MIN_LENGTH,
					}),
			),
			value: model.form.fields.name.$value,
			onChange: (e) => model.form.fields.name.change(e.target.value),
			required: true,
		},
	});

	const ParentAccountField = reflect({
		view: Select,
		bind: {
			'data-test-id': 'add-account-parent-account-select',
			value: model.form.fields.parentId.$value.map((value) => value?.toString() ?? null),
			onChange: (value: string | null) =>
				model.form.fields.parentId.change(value === null ? value : Number(value)),
			data: model.$accounts,
			disabled: model.form.fields.parentId.$disabled,
			label: translated('add-account.parent-account'),
			placeholder: translated('add-account.parent-account'),
			errorProps: { 'data-test-id': 'add-account-parent-account-select-error' },
			error: combine(
				[model.form.fields.parentId.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-account.parent-account'),
					}),
			),
			searchable: true,
			required: true,
		},
	});

	const CurrenciesField = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'add-account-currencies-select',
			value: model.form.fields.currencies.$value,
			onChange: model.form.fields.currencies.change,
			data: model.$currencies,
			disabled: model.form.fields.currencies.$disabled,
			label: translated('add-account.currencies'),
			placeholder: translated('add-account.currencies'),
			errorProps: { 'data-test-id': 'add-account-currencies-select-error' },
			error: combine(
				[model.form.fields.currencies.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-account.currencies'),
					}),
			),
			nothingFoundMessage: translated('add-account.currencies-nothing-found'),
			searchable: true,
			fullWidth: true,
		},
	});

	const NoteField = reflect({
		view: TextInput,
		bind: {
			'data-test-id': 'add-account-note-input',
			label: translated('add-account.note'),
			placeholder: translated('add-account.note'),
			errorProps: { 'data-test-id': 'add-account-note-input-error' },
			error: combine(
				[model.form.fields.note.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-account.note'),
						maxLength: ACCOUNT_NOTE_MAX_LENGTH,
					}),
			),
			value: model.form.fields.note.$value,
			onChange: (e) => model.form.fields.note.change(e.target.value),
		},
	});

	const CreateSubAccountsField = reflect({
		view: Checkbox,
		bind: {
			'data-test-id': 'add-account-create-subaccounts-checkbox',
			label: translated('add-account.write-subaccounts'),
			disabled: model.form.fields.canCreateSubAccounts.$disabled,
			checked: model.form.fields.canCreateSubAccounts.$value,
			onChange: (e) => model.form.fields.canCreateSubAccounts.change(e.target.checked),
		},
	});

	const WriteCashdesksFIeld = reflect({
		view: Checkbox,
		bind: {
			'data-test-id': 'add-account-write-cashdesks-checkbox',
			label: translated('add-account.write-cashdesks'),
			disabled: model.form.fields.canWriteCashdesks.$disabled,
			checked: model.form.fields.canWriteCashdesks.$value,
			onChange: (e) => model.form.fields.canWriteCashdesks.change(e.target.checked),
		},
	});

	const WriteUsersField = reflect({
		view: Checkbox,
		bind: {
			'data-test-id': 'add-account-write-users-checkbox',
			label: translated('add-account.write-users'),
			disabled: model.form.fields.canWriteUsers.$disabled,
			checked: model.form.fields.canWriteUsers.$value,
			onChange: (e) => model.form.fields.canWriteUsers.change(e.target.checked),
		},
	});

	const EditLimitsField = reflect({
		view: Checkbox,
		bind: {
			'data-test-id': 'add-account-edit-limits-checkbox',
			label: translated('add-account.edit-limits'),
			disabled: model.form.fields.canEditLimits.$disabled,
			checked: model.form.fields.canEditLimits.$value,
			onChange: (e) => model.form.fields.canEditLimits.change(e.target.checked),
		},
	});

	const BlockGamesField = reflect({
		view: Checkbox,
		bind: {
			'data-test-id': 'add-account-block-games-checkbox',
			label: translated('add-account.block-games'),
			disabled: model.form.fields.canBlockGames.$disabled,
			checked: model.form.fields.canBlockGames.$value,
			onChange: (e) => model.form.fields.canBlockGames.change(e.target.checked),
		},
	});

	const EditGameSettingsField = reflect({
		view: Checkbox,
		bind: {
			'data-test-id': 'add-account-edit-game-settings-checkbox',
			label: translated('add-account.edit-game-settings'),
			disabled: model.form.fields.canEditGameSettings.$disabled,
			checked: model.form.fields.canEditGameSettings.$value,
			onChange: (e) => model.form.fields.canEditGameSettings.change(e.target.checked),
		},
	});

	const AddAccountModalButton = reflect({
		view: ModalForm.Button,
		bind: {
			'data-test-id': 'add-account-modal-button',
			leftSection: <Plus />,
			className: cls.addAccountModalButton,
			disabled: not(viewerModel.$canCreateSubAccounts),
			children: translated('add-account.label'),
			modalData: null,
		},
	});

	const AddAccountModal = forwardRef<HTMLDivElement, ModalFormProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		const pending = useUnit(model.$pending);

		return (
			<ModalForm
				ref={ref}
				title={t('add-account.title')}
				cancelButtonProps={{
					children: t('add-account.cancel'),
				}}
				submitButtonProps={{
					children: t('add-account.add'),
					loading: pending,
				}}
				{...props}
			>
				<NameField />
				<ParentAccountField />
				<CurrenciesField />
				<NoteField />
				<Stack gap={12}>
					<Text
						size='sm'
						fw={500}
					>
						{t('add-account.permissions')}
					</Text>
					<CreateSubAccountsField />
					<WriteCashdesksFIeld />
					<WriteUsersField />
					<EditLimitsField />
					<EditGameSettingsField />
					<BlockGamesField />
				</Stack>
			</ModalForm>
		);
	});

	return Object.assign(AddAccountModal, {
		Button: AddAccountModalButton,
	});
};
